import './clan-arena.css';
import { Swords, Ticket, Crown, Shield } from 'lucide-react';
import {
  GoToClanButton,
  Header,
  SeasonTimer,
  Spin,
  TokenSelector,
} from '../../../components';
import { useLeague, useLeaguePlayerState, useUserData } from '../../../hooks';
// import { ROUTES } from '../../../routes';
import {
  ArenaInfoLayoutProps,
  ArenaInfoProps,
  ArenaLayoutProps,
  ArenaLeftProps,
  ArenaMainGridProps,
  ArenaRewardsInfoProps,
  ArenaRightProps,
  ArenaSelectedTokensProps,
  ArenaStatusTicketsProps,
  ClanArenaProps,
} from './clan-arena.props';

export const ClanArena = ({ league, userInClan = false }: ClanArenaProps) => {
  const { currentSeason, loading } = useLeague();

  const { getArenaStats } = useUserData();

  const { arena, selectToken } = useLeaguePlayerState();

  const stats = getArenaStats({
    leagueId: league.id,
    seasonId: `${currentSeason?.seasonNumber ?? 0}`,
  });

  const available = stats.available;
  const claimed = stats.claimed;
  const totalEarned = stats.totalEarned;

  // MAX TOKENS DISPONIBLES EN LA ARENA (supply global)
  const maxTokens = currentSeason?.arena?.baseTokens ?? 0;

  const handleSelectToken = async (token: number) => {
    await selectToken(token, 'arena');
  };

  if (!currentSeason || loading)
    return (
      <div className="league-page-loading-container">
        <Spin />
      </div>
    );

  // Si no está en un clan, mostrar pantalla bloqueada
  if (!userInClan) return <ArenaBlocked />;

  return (
    <>
      <ArenaLayout>
        <ArenaHeader />

        <ArenaInfoLayout>
          <SeasonTimer />

          <ArenaInfo league={league} />

          <ArenaMainGrid>
            <ArenaLeft>
              <ArenaCard maxTokens={maxTokens} />

              <ArenaStatusTickets
                totalEarned={totalEarned}
                claimed={claimed}
                available={available}
              />

              <ArenaSelectedTokens selectedTokens={arena.selectedTokens} />
            </ArenaLeft>

            <ArenaRight>
              <TokenSelector
                mode="arena"
                maxTokens={maxTokens}
                availableSelections={arena.available}
                selectedTokens={arena.selectedTokens}
                onSelectToken={handleSelectToken}
              />
            </ArenaRight>
          </ArenaMainGrid>

          <ArenaRewardsInfo league={league} />
        </ArenaInfoLayout>
      </ArenaLayout>
    </>
  );
};

const ArenaBlocked = () => (
  <div className="arenaBlocked-container">
    <div className="arenaBlocked-content">
      <Shield className="arenaBlocked-icon" stroke="#374151" />

      <h1 className="arenaBlocked-title">Arena Bloqueada</h1>

      <p className="arenaBlocked-description">
        La Arena del Clan es exclusiva para miembros de clanes. Únete a un clan
        para desbloquear este modo de juego y competir por premios adicionales.
      </p>

      <GoToClanButton />
    </div>
  </div>
);

const ArenaLayout = ({ children }: ArenaLayoutProps) => {
  return <div className="arenaLayout-container">{children}</div>;
};

const ArenaHeader = () => {
  // const { league } = useLeague();

  return (
    <>
      <Header
        // goBack={{
        //   path: `${ROUTES.LEAGUE}/${league?.path}`,
        //   text: `Volver a ${league?.name}`,
        // }}
        titleComponent={{
          component: (
            <div className="arenaHeader-right">
              <div className="arenaHeader-badgeIconWrapper">
                <Swords className="arenaHeader-badgeIcon" stroke="#000011" />
              </div>

              <div>
                <div className="arenaHeader-badgeText">MODO EXCLUSIVO</div>
                <h1 className="arenaHeader-title">Arena del Clan</h1>
              </div>
            </div>
          ),
        }}
      />
    </>
  );
};

export const ArenaInfoLayout = ({ children }: ArenaInfoLayoutProps) => {
  return <div className="arenaInfoLayout-container">{children}</div>;
};

const ArenaInfo = ({ league }: ArenaInfoProps) => (
  <div className="arenaInfo-container">
    <div className="arenaInfo-glowTop" />
    <div className="arenaInfo-glowBottom" />

    <div className="arenaInfo-content">
      <div className="arenaInfo-header">
        <Crown className="arenaInfo-icon" stroke="#CAFB10" />

        <div>
          <h2 className="arenaInfo-title">
            Competencia Paralela de {league.name}
          </h2>

          <p className="arenaInfo-description">
            Usa tus tickets de batalla para seleccionar tokens únicos en esta
            arena exclusiva del clan. Los premios son independientes y
            adicionales a la liga principal.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const ArenaMainGrid = ({ children }: ArenaMainGridProps) => {
  return <div className="arenaMainGrid-container">{children}</div>;
};

const ArenaLeft = ({ children }: ArenaLeftProps) => {
  return <div className="arenaLeft-container">{children}</div>;
};

const ArenaRight = ({ children }: ArenaRightProps) => {
  return <div className="arenaRight-container">{children}</div>;
};

const ArenaCard = ({ maxTokens }: { maxTokens: number }) => {
  const { currentSeason } = useLeague();
  const { cardName, cardImg } = currentSeason?.arena ?? {
    cardName: 'Batalla del Clan',
  };

  return (
    <div className="arenaCard-container">
      <div className="arenaCard-header">
        <Swords className="arenaCard-icon" stroke="#CAFB10" />
        <h3 className="arenaCard-title">Tarjeta Arena del Clan</h3>
      </div>

      <div className="arenaCard-visual">
        <div className="arenaCard-overlay" />

        <div className="arenaCard-top">
          <div className="arenaCard-badge">ARENA EXCLUSIVA</div>
          <div className="arenaCard-name">{cardName}</div>
        </div>

        {cardImg ? (
          <>
            <div className="arenaCard-display-img-container">
              <div className="arenaCard-display-darklayer" />
              <img
                className="arenaCard-display-img"
                src={cardImg}
                alt="TARJETA"
              />
            </div>
          </>
        ) : (
          <div className="arenaCard-emoji">⚔️</div>
        )}

        <div className="arenaCard-bottom">
          <div className="arenaCard-tokenBox">
            <div className="arenaCard-tokenLabel">Tokens Arena</div>
            <div className="arenaCard-tokenValue">
              #1 - #{maxTokens.toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      <div className="arenaCard-footer">
        Competencia exclusiva para miembros de clanes
      </div>
    </div>
  );
};

const ArenaStatusTickets = ({
  totalEarned,
  claimed,
  available,
}: ArenaStatusTicketsProps) => (
  <div className="arenaStatusTickets-container">
    <h3 className="arenaStatusTickets-title">
      <Ticket className="arenaStatusTickets-icon" stroke="#CAFB10" />
      Estado de Tickets
    </h3>

    <div className="arenaStatusTickets-list">
      <div className="arenaStatusTickets-row">
        <span className="arenaStatusTickets-label">Tickets Totales:</span>
        <span className="arenaStatusTickets-value arenaStatusTickets-total">
          {totalEarned}
        </span>
      </div>

      <div className="arenaStatusTickets-row">
        <span className="arenaStatusTickets-label">Usados:</span>
        <span className="arenaStatusTickets-value arenaStatusTickets-used">
          {claimed}
        </span>
      </div>

      <div className="arenaStatusTickets-divider" />

      <div className="arenaStatusTickets-row">
        <span className="arenaStatusTickets-availableLabel">Disponibles:</span>

        <span
          className={`arenaStatusTickets-availableValue ${
            available > 0
              ? 'arenaStatusTickets-availableOk'
              : 'arenaStatusTickets-availableEmpty'
          }`}
        >
          {available}
        </span>
      </div>
    </div>
  </div>
);

const ArenaSelectedTokens = ({ selectedTokens }: ArenaSelectedTokensProps) => {
  if (selectedTokens.length === 0) return null;

  return (
    <div className="arenaSelectedTokens-container">
      <h3 className="arenaSelectedTokens-title">Tokens Seleccionados:</h3>

      <div className="arenaSelectedTokens-list">
        {selectedTokens.map((token) => (
          <span key={token} className="arenaSelectedTokens-item">
            #{token}
          </span>
        ))}
      </div>
    </div>
  );
};

const ArenaRewardsInfo = ({ league }: ArenaRewardsInfoProps) => (
  <div className="arenaRewardsInfo-container">
    <div className="arenaRewardsInfo-header">
      <Crown className="arenaRewardsInfo-icon" stroke="#CAFB10" />

      <div className="arenaRewardsInfo-content">
        <h3 className="arenaRewardsInfo-title">
          Premios Exclusivos de la Arena
        </h3>

        <p className="arenaRewardsInfo-description">
          Los premios de la Arena del Clan son independientes y se suman a los
          de {league.name}. Al final de la temporada, los 3 primeros lugares
          recibirán premios especiales.
        </p>
      </div>
    </div>
  </div>
);
